import { createSelector } from '@reduxjs/toolkit';

import type { HexColor } from '@disy/lib/colors';
import { assert, assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { compareWithLocale } from '@disy/lib/locale';
import { entries } from '@disy/lib/type-util';
import { getDefaultSetting, getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';
import type { LoadingState } from '@disy/workbook.view/loading-state';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import { hasCapability } from 'cadenza/data-source-util';
import { isFeatureAvailable } from 'cadenza/features';
import type { DataSourceCapability } from 'cadenza/model/data-source-capability.gen';
import {
  type ObjecttypeId,
  type ObjecttypeIdString,
  asObjecttypeId,
  asObjecttypeIdString,
  isValidMainObjecttype,
  objecttypeIdToString,
} from 'cadenza/repository/model/objecttype-id.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import { itemIdToString, repositoryItemIdEquals } from 'cadenza/repository/model/repository-item-id';
import type { WorkbookEmbeddingTargetId } from 'cadenza/repository/model/workbook-embedding-target-id.gen';
import { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import { type RootStateOr, getStore } from 'cadenza/store/cadenza-store';
import { theme } from 'cadenza/theming-api';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { getViewType, isAnalyticLine } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import { isObjecttypeBound } from 'cadenza/workbook/api/objecttype';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import {
  areAllHidden,
  conditionHasValues,
  getDeactivated,
  hasValuesSet,
  isDeactivatedForWorksheet,
  isHidden,
  isMandatory,
  isSpatialConditionWithValueItems,
} from 'cadenza/workbook/conditions/condition-util';
import type { FeatureInfoHeadlineType } from 'cadenza/workbook/feature-info-settings-dialog/feature-info-headline-preview/feature-info-headline-preview';
import type { AttributeBundleName, AttributesByBundle } from 'cadenza/workbook/model/attribute-bundle-name';
import { AttributeBundleSettingsKey } from 'cadenza/workbook/model/attribute-bundle-settings-key.gen';
import { createAttributeId, getBaseAttributeId, isPartOfObjecttype } from 'cadenza/workbook/model/attribute-id-util';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { CombinationChartDataViewVisualizationKeys } from 'cadenza/workbook/model/combination-chart-visualization-types.gen';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionGroupDto } from 'cadenza/workbook/model/condition-group-dto';
import type { ConditionGroupId } from 'cadenza/workbook/model/condition-group-id.gen';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key.gen';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import type { NotificationType } from 'cadenza/workbook/notifications/workbook-notifications';
import { isDataViewNotification, isLayerNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import type { AttributeDescriptionVisibility } from 'cadenza/workbook/view/view-info/attribute-description-visibility';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import type { DataViewMetadata, ViewMetadata, ViewWithMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';

import i18n from './workbook-core-selectors.properties';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';

// Selectors

export const getWorkbookConfiguration = (state: RootStateOr<WorkbookState>) => slice(state).workbookConfiguration;
export const getCouplingLayerLimit = (state: RootStateOr<WorkbookState>) =>
  slice(state).workbookConfiguration!.restrictions.couplingLayerLimit;
export const getInitializationStatus = (state: RootStateOr<WorkbookState>) => slice(state).initializationStatus;
export const getCommandStatus = (state: RootStateOr<WorkbookState>) => slice(state).commandStatus;
export const getObjecttypeDataDependencies = (state: RootStateOr<WorkbookState>) =>
  slice(state).objecttypeDataDependencies;
export const getObjecttypeDataDependenciesByObjecttypeId = (
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeIdString,
) => getObjecttypeDataDependencies(state).dependencyStateByObjecttypeId[objecttypeId];
export const getSnapshotDeletionPeriod = (state: RootStateOr<WorkbookState>) =>
  slice(state).workbookConfiguration!.temporaryLinkSettings.snapshotDeletionPeriod;

export const getWorkbookName = (state: RootStateOr<WorkbookState>) => slice(state).workbookName;
export const getWorkbookNameAndHash = (state: RootStateOr<WorkbookState>) => {
  const workbookNameAndHash = slice(state).workbookNameAndHash;
  assertNonNullable(workbookNameAndHash);
  return workbookNameAndHash;
};
export const getWorkbookVersion = (state: RootStateOr<WorkbookState>) => slice(state).version;
export const getWorkbookPublicationStage = (state: RootStateOr<WorkbookState>) => slice(state).publicationStage;
export const isWorkbookPublished = (state: RootStateOr<WorkbookState>) =>
  getWorkbookPublicationStage(state) === 'PUBLISHED';
export const getActiveWorksheetId = (state: RootStateOr<WorkbookState>) => slice(state).activeWorksheetId!;
export const getWorksheetById = (state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) =>
  slice(state).worksheetById[worksheetId];

export const isRepositoryPublishingProcessEnabled = (state: RootStateOr<WorkbookState>) =>
  slice(state).repository.publishingProcessEnabled;

export const isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled = (state: RootStateOr<WorkbookState>) => {
  return !isRepositoryPublishingProcessEnabled(state) || isWorkbookPublished(state);
};

export function getWorkbookWindowType(state: RootStateOr<WorkbookState>) {
  return slice(state).windowType;
}

export function getWindowsSyncChannelName(state: RootStateOr<WorkbookState>) {
  return slice(state).windowSyncChannelName;
}

export const isWorkbookDeletionDeadlineEnabled = (state: RootStateOr<WorkbookState>) =>
  slice(state).repository.deletionDeadlineWorkbookEnabled;

/**
 * Returns all worksheets of the workbook.
 */
export const getWorksheets = (state: RootStateOr<WorkbookState>) => {
  const worksheetIds = slice(state).worksheets;
  return worksheetIds.map((id) => getWorksheetById(state, id));
};

/**
 * <p>
 *     <b>Important note</b><br/>
 *     Be aware that the concept of active worksheet does not exist in reporting, the
 *     method will return <code>undefined</code>. Consider this in direct calls as well
 *     as selectors that make use of this. It is safer to use {@link getWorksheetByViewId}
 *     if you have access to the viewId.
 * </p>
 * @param state
 */
export const getActiveWorksheet = (state: RootStateOr<WorkbookState>) =>
  getWorksheetById(state, getActiveWorksheetId(state));
export const getWorksheetByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getWorksheets(state).find((worksheet) => worksheet.views.includes(viewId));

export const getWorkbookRepositoryName = (state: RootStateOr<WorkbookState>) => slice(state).repository.name;
export const workbookRepositoryHasCmdb = (state: RootStateOr<WorkbookState>) =>
  !!slice(state).repository.cadenzaManagedDB;
export const getWorkbookRepository = (state: RootStateOr<WorkbookState>) => slice(state).repository;
export const getWorkbookId = (state: RootStateOr<WorkbookState>): WorkbookId =>
  WorkbookId.of(getWorkbookRepositoryName(state), getWorkbookName(state));
export const getWorkbookPrivileges = (state: RootStateOr<WorkbookState>) => slice(state).privileges;
export const getWorkbookPrintName = (state: RootStateOr<WorkbookState>) => slice(state).printName;
export const getWorkbookType = (state: RootStateOr<WorkbookState>) => slice(state).workbookType;
export const getWorkbookRelation = (state: RootStateOr<WorkbookState>) => slice(state).relation;
export const isWorkbookSaved = (state: RootStateOr<WorkbookState>) => slice(state).saved;
export const isWorkbookChanged = (state: RootStateOr<WorkbookState>) =>
  slice(state).changed !== 'no' || slice(state).clientChanged;
export const hasMajorChange = (state: RootStateOr<WorkbookState>) => slice(state).changed === 'major';
export const isWorkbookVisible = (state: RootStateOr<WorkbookState>) =>
  hasPrivilege(slice(state).privileges, 'visible');
export const isWorkbookVisibleRestricted = (state: RootStateOr<WorkbookState>) =>
  hasPrivilege(slice(state).privileges, 'visibleRestricted');
export const isWorkbookSharable = (state: RootStateOr<WorkbookState>) => hasPrivilege(slice(state).privileges, 'share');
export const isWorkbookWritable = (state: RootStateOr<WorkbookState>) => hasPrivilege(slice(state).privileges, 'write');
export const isWorkbookVisibilityRestricted = (state: RootStateOr<WorkbookState>) =>
  isVisibilityRestricted(slice(state).privileges); // Also see WorkbookContextService.isWorkbookVisibilityRestricted - Same implementation in frontend and backend
export const getAttributeDescriptionVisibility = (state: RootStateOr<WorkbookState>): AttributeDescriptionVisibility =>
  slice(state).config!.attributeDescriptionVisibility;
export const areRequiredConditionsValid = createSelector(
  getActiveWorksheet,
  (worksheet) => worksheet.requiredConditionsValid,
);

/**
 * Checks if the workbook is empty.
 * _Note_: Here, empty refers to having no views, no data sources, no object types and only one worksheet.
 * Workbook settings like drillthrough targets, the worksheet names etc. are not taken into account here.
 */
export const isWorkbookEmpty = (state: RootStateOr<WorkbookState>) =>
  getViewIds(state).length === 0 &&
  getDataSources(state).length === 0 &&
  getObjecttypes(state).length === 0 &&
  getWorksheets(state).length === 1;

/**
 * Returns the workbook specific drill-through targets (not including the legacy targets
 * defined via Cadenza Desktop)
 */
export const getDrillThroughTargets = (state: RootStateOr<WorkbookState>) => slice(state).drillThroughTargets;

/**
 * Returns all curated reports
 *
 * @param state - The state object
 * @return All reports
 */
export const getReports = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).reportById,
  (reports) => Object.values(reports).sort((a, b) => compareWithLocale(a.printName, b.printName)),
);

/**
 * Returns all workbook embedding targets
 *
 * @param state - The state object
 * @return All embedding targets
 */
export const getEmbeddingTargets = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).embeddingTargetById,
  Object.values,
);

/**
 * Returns workbook embedding target with given ID
 *
 * @param state - The state object
 * @param id
 * @return WorkbookEmbeddingTargetDto
 */
export const getEmbeddingTargetById = (state: RootStateOr<WorkbookState>, id: WorkbookEmbeddingTargetId) =>
  slice(state).embeddingTargetById[id];

/**
 * Return ReportDTO with given ID
 */
export const getReportById = (state: RootStateOr<WorkbookState>, reportId: ReportId) =>
  slice(state).reportById[reportId];

/**
 * Returns the background color for the active or a specific worksheet if its background color setting is set, otherwise the
 * first existing background color of any previous worksheet ("auto"-setting behavior). If no previous
 * worksheet has a background color then the default color is returned which is defined in theming.
 * <p>Pass autoOnly: true if you want to get the "auto"-color for a worksheet regardless of its own
 * background color settings.</p>
 *
 * @param state - The state object.
 * @param options - Options object
 * @param [options.worksheetId] - Pass a worksheetId to get the background color of a specific worksheet other than the currently active one.
 * @param [options.autoOnly] - If true then the "auto" background color is returned.
 * @return The calculated background color of the worksheet.
 */
export const getWorksheetBackgroundColor = (
  state: RootStateOr<WorkbookState>,
  {
    worksheetId,
    autoOnly,
  }: {
    worksheetId?: WorksheetId;
    autoOnly?: boolean;
  } = {},
): HexColor => {
  const worksheets = getWorksheets(state);
  const worksheet = worksheetId ? worksheets.find((ws) => ws.id === worksheetId) : getActiveWorksheet(state);
  if (worksheet) {
    const getWsBackgroundColor = (ws: WorksheetDto) => {
      // Get worksheet background color without "auto"
      return getSetting<HexColor>(ws, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR'));
    };

    let color = getWsBackgroundColor(worksheet);
    if (color != null && !autoOnly) {
      return color;
    }

    const worksheetIndex = worksheets.map((ws) => ws.id).indexOf(worksheet.id);
    const previousWorksheets = worksheets.slice(0, worksheetIndex);
    while (previousWorksheets.length > 0) {
      color = getWsBackgroundColor(previousWorksheets.pop()!);
      if (color != null) {
        return color;
      }
    }
  }
  return theme('worksheet-bg');
};

export const getAttributes = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).attributeById);
export const getAttributeById = (state: RootStateOr<WorkbookState>, attributeId: AttributeId | ActiveAttributeId) => {
  return (
    slice(state).activeAttributeById[attributeId as ActiveAttributeId] ||
    slice(state).attributeById[attributeId as AttributeId]
  );
};
export const getAttributesById = (
  state: RootStateOr<WorkbookState>,
  attributeIds: (AttributeId | ActiveAttributeId)[],
) => {
  return attributeIds.map((attrId) => getAttributeById(state, attrId));
};

export const getAttributesByBundle = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
): AttributesByBundle => {
  const dataView = getDataViewRequired(state, dataViewId);
  return Object.fromEntries(
    Object.entries(dataView.attributes).map(([bundle, attributeIds]) => [
      bundle,
      attributeIds.map((attributeId) => getAttributeById(state, attributeId)),
    ]),
  );
};

export const getAttributeByName = (state: RootStateOr<WorkbookState>, attributeName: string) => {
  return getAttributes(state).find((attr) => attr.name === attributeName);
};

export const getAttributeIdsFromBundle = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView.attributes[bundleName] ?? [];
};

export function hasBundleAttributes(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) {
  return getAttributeIdsFromBundle(state, dataViewId, bundleName).length !== 0;
}

export function hasAttributeBundle(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) {
  return !!getAttributeBundleDefinition(state, dataViewId, bundleName);
}

export const getAttributesFromBundles = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  ...bundleNames: AttributeBundleName[]
) => {
  const dataView = getDataViewById(state, dataViewId);
  return bundleNames
    .flatMap((bundleName) => dataView?.attributes[bundleName] || [])
    .map((attributeId) => getAttributeById(state, attributeId));
};

export function getAttributeSetting(
  state: RootStateOr<WorkbookState>,
  attributeId: ActiveAttributeId,
  persistenceKey: string,
  defaultValue?: string,
) {
  return getSetting(getAttributeById(state, attributeId), persistenceKey, defaultValue);
}

// This selector returns all object types, including ones with restricted visibility.
// In most cases only visible unrestricted object types are required, so use getVisibleObjecttypes or getVisibleObjecttypeIds instead.
export const getObjecttypes: (state: RootStateOr<WorkbookState>) => Objecttype[] = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).objecttypeById,
  Object.values,
);

export const getUnavailableObjecttypeIds = (state: RootStateOr<WorkbookState>) => slice(state).unavailableObjecttypeIds;

const getUsedAttributes = createSelector(
  [
    (state) => slice(state).dataViewById,
    (state) => slice(state).conditionById,
    (state) => slice(state).drillThroughTargets,
  ],
  (dataViewById, conditionById, drillThroughTargets) =>
    new Set([
      ...Object.values(dataViewById).flatMap((dv) => Object.values(dv.attributes).flat().map(getBaseAttributeId)),
      ...Object.values(conditionById).flatMap((c) => [
        ...c.attributeIds,
        ...(c.conditionConfiguration && 'additionalAttributes' in c.conditionConfiguration ?
          c.conditionConfiguration.additionalAttributes
        : []),
      ]),
      ...Object.values(drillThroughTargets).map((dt) => dt.attributeId),
    ]),
);

export const isObjecttypeUsed = (state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) =>
  [...getUsedAttributes(state)].some((id) => isPartOfObjecttype(id, objecttypeId)) ||
  Object.values(slice(state).dataViewById).some((dataView) =>
    repositoryItemIdEquals(dataView.mainObjecttype.objecttypeId, asObjecttypeId(objecttypeId)),
  );

export const isAttributeUsed = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) =>
  getUsedAttributes(state).has(attributeId);

export const getDataProtection = (state: RootStateOr<WorkbookState>) => {
  return slice(state).dataProtection;
};
export const getObjecttypeById = (
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeId | ObjecttypeIdString,
) => {
  return slice(state).objecttypeById[asObjecttypeIdString(objecttypeId)];
};

export const getAllAttributesFromObjecttype = createSelector(
  [getObjecttypeById, (state) => slice(state).attributeById, (state) => slice(state).objecttypeById],
  (objecttype, attributesById, objecttypeById) => {
    const attributes = objecttype.attributes.map((attributeId) => attributesById[attributeId]);
    const attributesFromEnrichments =
      objecttype.enrichments?.flatMap((enrichment) => {
        const enrichmentObjecttype = objecttypeById[enrichment.objecttypeId];
        return enrichmentObjecttype.attributes.map((attributeId) => attributesById[attributeId]);
      }) ?? [];
    attributes.push(...attributesFromEnrichments);
    return attributes;
  },
);

export const getAllEnrichmentsFromObjecttype = createSelector(
  [getObjecttypeById],
  (objecttype) => objecttype?.enrichments ?? [],
);

export const getAllEnrichmentObjecttypeIds = createSelector([(state) => slice(state).objecttypeById], (objecttypes) =>
  Object.values(objecttypes)
    .flatMap((ot) => ot.enrichments ?? [])
    .map((enrichment) => enrichment.objecttypeId),
);

export const getNotVisibilityRestrictedObjecttypes = createSelector(getObjecttypes, (objecttypes) =>
  objecttypes
    .filter((objecttype) => !isVisibilityRestricted(objecttype.currentUserPrivileges))
    .sort((a, b) => compareWithLocale(a.printName, b.printName)),
);

export const getVisibleObjecttypes = createSelector(getNotVisibilityRestrictedObjecttypes, (objecttypes) =>
  objecttypes.filter((objecttype) => objecttype.visible),
);
export const getVisibleObjecttypeIds = createSelector(getVisibleObjecttypes, (visibleObjecttypes) =>
  visibleObjecttypes.map(({ id }) => id),
);

export function resolveObjecttypeAttributes(
  objecttypes: Objecttype[] | ObjecttypeWithAttributes[],
): ObjecttypeWithAttributes[] {
  return objecttypes.map((objecttype) => {
    return {
      ...objecttype,
      attributes: objecttype.attributes
        .map((attributeOrId) =>
          typeof attributeOrId === 'string' ? getAttributeById(getStore().getState(), attributeOrId) : attributeOrId,
        )
        // The attribute can be null when it's being removed, hence the null check
        .filter((attribute) => attribute?.visible),
    };
  });
}

export const getObjecttypesWithAttributes = createSelector(getObjecttypes, resolveObjecttypeAttributes);
export const getVisibleObjecttypesWithoutTheirEnrichments = createSelector(getVisibleObjecttypes, (objecttypes) => {
  const visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds = [
    ...new Set(
      objecttypes.flatMap((objecttype) => objecttype.enrichments ?? []).map((enrichment) => enrichment.objecttypeId),
    ),
  ];
  return objecttypes
    .filter((objecttype) => !visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds.includes(objecttype.id))
    .sort((a, b) => compareWithLocale(a.printName, b.printName));
});

function isVisibilityRestricted(privileges: ObjectPrivilege[]) {
  return privileges?.length > 0 && !hasPrivilege(privileges, 'visible');
}

export const getObjecttypeByAttributeIdMapping = createSelector(
  getObjecttypes,
  (objecttypes) =>
    Object.fromEntries(
      objecttypes.flatMap((objecttype) => objecttype.attributes.map((attributeId) => [attributeId, objecttype])),
    ) as Record<AttributeId, Objecttype>,
);

export const getObjecttypeByAttributeId = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) => {
  return getObjecttypeByAttributeIdMapping(state)[attributeId];
};

const getEnrichmentsByAttributeId = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).objecttypeById,
  (objecttypeById) => {
    return Object.fromEntries(
      Object.values(objecttypeById)
        .flatMap((objecttype) => objecttype.enrichments)
        .flatMap((enrichment) =>
          enrichment ?
            objecttypeById[enrichment.objecttypeId].attributes.map((attributeId) => [attributeId, enrichment])
          : [],
        ),
    );
  },
);

export const getEnrichmentByAttributeId = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) => {
  return getEnrichmentsByAttributeId(state)[attributeId];
};

/**
 * Returns, in case the attributeId belongs to an objecttype which is an enrichment,
 * the enrichedObjecttypeId of this enrichment. Else, the objecttypeId for this attributeId
 */
export const getEnrichedObjecttypeOrObjecttypeIdByAttributeId = (
  state: RootStateOr<WorkbookState>,
  attributeId: AttributeId,
) => {
  const enrichment = getEnrichmentByAttributeId(state, attributeId);
  if (enrichment) {
    return enrichment.enrichedObjecttypeId;
  }
  return getObjecttypeByAttributeId(state, attributeId).id;
};

export const hasObjecttypeWithObjectId = (
  state: RootStateOr<WorkbookState>,
  objecttypeIdString: ObjecttypeIdString,
) => {
  return !!getObjecttypeById(state, objecttypeIdString);
};

export const hasObjecttypeWithPrintName = (state: RootStateOr<WorkbookState>, name: string) => {
  return getObjecttypes(state).some((ot) => ot.printName === name);
};

export const hasBoundObjecttypes = (state: RootStateOr<WorkbookState>) => {
  return getObjecttypes(state).some((ot) => isObjecttypeBound(ot));
};

export const getDataSources = (state: RootStateOr<WorkbookState>) => slice(state).dataSources;

export const getVisibleDataSources = createSelector(getDataSources, getVisibleObjecttypes, filterEmptyDatasources);

function filterEmptyDatasources(dataSources: WorkbookDataSourceDto[], visibleObjecttypes: Objecttype[]) {
  return dataSources.filter(({ itemId }) =>
    visibleObjecttypes.some(({ dataSourceName }) => dataSourceName === itemId.itemName),
  );
}

export function getDataSourceByObjecttypeId(
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeId | ObjecttypeIdString,
) {
  const objecttype = getObjecttypeById(state, objecttypeId);
  return getDataSources(state).find(
    (dataSource) =>
      dataSource.itemId.repositoryName === objecttype.dataSourceRepositoryName &&
      dataSource.itemId.itemName === objecttype.dataSourceName,
  )!;
}

export function hasObjecttypeDatasourceCapabilities(
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeId | ObjecttypeIdString,
  capabilities: DataSourceCapability[],
) {
  const dataSource = getDataSourceByObjecttypeId(state, objecttypeId);
  return capabilities.every((capability) => hasCapability(dataSource, capability));
}

export function getProjectById(state: RootStateOr<WorkbookState>, projectId: ProjectId) {
  return slice(state).projectById[itemIdToString(projectId)];
}

/** Returns the workbook's ProjectId */
export function getProject(state: RootStateOr<WorkbookState>) {
  const projectId = slice(state).project;
  return projectId && getProjectById(state, projectId);
}

export function isObjecttypeInCmdb(state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) {
  return getDataSourceByObjecttypeId(state, objecttypeId).cmdb;
}

export const getConditionsAndGroups = (state: RootStateOr<WorkbookState>) => slice(state).conditionsAndGroups;
export const getConditionById = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  slice(state).conditionById[conditionId];

export const getConditionsByGroupId = (state: RootStateOr<WorkbookState>, conditionGroupId: ConditionGroupId) =>
  getConditionGroupConditionIds(state, conditionGroupId).map((conditionId) => getConditionById(state, conditionId));

export const getConditionDeactivated = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  getDeactivated(getConditionById(state, conditionId));

export const isConditionDeactivatedForActiveWorksheet = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isDeactivatedForWorksheet(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionHidden = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isHidden(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionGroupHidden = (state: RootStateOr<WorkbookState>, conditionGroupId: ConditionGroupId) =>
  areAllHidden(getConditionsByGroupId(state, conditionGroupId), getActiveWorksheetId(state));

export const isConditionMandatory = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isMandatory(getConditionById(state, conditionId));

/** @knipignore */ // WIP, remove ignore once used.
export const isConditionGroupOrAnyConditionMandatory = (
  state: RootStateOr<WorkbookState>,
  conditionGroupId: ConditionGroupId,
) =>
  getConditionGroupById(state, conditionGroupId).mandatory ||
  getConditionGroupConditionIds(state, conditionGroupId).find((conditionId) =>
    isMandatory(getConditionById(state, conditionId)),
  );

export function getAffectedViewAndDataViewByHierarchyRelations(
  state: RootStateOr<WorkbookState>,
  hierarchyId: HierarchyId,
) {
  const activeWorksheetId = getActiveWorksheetId(state);
  const relations = slice(state).affectedViewsByHierarchyId[hierarchyId];
  return {
    affectedViews: relations?.affectedViews?.[activeWorksheetId] ?? [],
    affectedDataViews: relations?.affectedDataViews?.[activeWorksheetId] ?? [],
  };
}

export function getAllAffectedViewIdsByHierarchy(state: RootStateOr<WorkbookState>) {
  return slice(state).affectedViewsByHierarchyId;
}

function getConditionsInAnalysisContext(state: RootStateOr<WorkbookState>) {
  const result: ConditionId[] = [];
  for (const id of slice(state).conditionsAndGroups) {
    result.push(
      ...(slice(state).conditionById[id as ConditionId] ?
        [id as ConditionId]
      : slice(state).conditionGroupById[id as ConditionGroupId].conditions),
    );
  }
  return result;
}

export function isAnalysisContextAffectingViews(state: RootStateOr<WorkbookState>) {
  return getConditionsInAnalysisContext(state).some(
    (conditionId) =>
      !(isConditionHidden(state, conditionId) || isConditionDeactivatedForActiveWorksheet(state, conditionId)) &&
      getViewIdsAffectedByCondition(state, conditionId).length > 0 &&
      hasValuesSet(getConditionById(state, conditionId)),
  );
}

export function getAffectedViewAndDataViewRelations(state: RootStateOr<WorkbookState>, conditionId: ConditionId) {
  const activeWorksheetId = getActiveWorksheetId(state);
  const relations = slice(state).affectedViewsByConditionId[conditionId];
  return {
    affectedViews: relations?.affectedViews?.[activeWorksheetId] ?? [],
    affectedDataViews: relations?.affectedDataViews?.[activeWorksheetId] ?? [],
    affectedViewsWithLocalConditions: relations?.affectedViewsWithLocalConditions?.[activeWorksheetId] ?? [],
    affectedDataViewsWithLocalConditions: relations?.affectedDataViewsWithLocalConditions?.[activeWorksheetId] ?? [],
  };
}

export function isDataViewAffectedByCondition(
  state: RootStateOr<WorkbookState>,
  conditionId: ConditionId,
  dataViewId: DataViewId,
) {
  return getAffectedViewAndDataViewRelations(state, conditionId).affectedDataViews.includes(dataViewId);
}

export function getViewIdsAffectedByCondition(
  state: RootStateOr<WorkbookState>,
  conditionId: ConditionId,
  withLocalConditions = false,
) {
  return (
    slice(state).affectedViewsByConditionId[conditionId]?.[
      withLocalConditions ? 'affectedViewsWithLocalConditions' : 'affectedViews'
    ]?.[getActiveWorksheetId(state)] ?? []
  );
}

function getDataViewIdsAffectedByCondition(state: RootStateOr<WorkbookState>, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]?.affectedDataViews?.[getActiveWorksheetId(state)] ?? [];
}

export function getAttributesByConditionId(state: RootStateOr<WorkbookState>, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]?.affectedAttributesByDataViewId ?? {};
}

export const getConditionsById = (state: RootStateOr<WorkbookState>) => slice(state).conditionById;
export const getConditionGroupById = (state: RootStateOr<WorkbookState>, conditionId: ConditionGroupId) =>
  slice(state).conditionGroupById[conditionId];
export const getConditionGroups = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).conditionGroupById);

export const getConditionGroupConditionIds = (state: RootStateOr<WorkbookState>, conditionId: ConditionGroupId) =>
  getConditionGroupById(state, conditionId).conditions;
export const getSpatialConditionAttributeForDataView = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const attributesByBundleElement = requireNonNullable(
    getAttributesByBundle(state, dataViewId)['SPATIAL_FILTER'],
    'Not a map dataview',
  );
  return (
    attributesByBundleElement[0] ||
    getAttributeById(state, getDataViewById(state, dataViewId).defaultSpatialGeometryAttributeId)
  );
};

export const getHierarchies = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).hierarchyById);

export const isGlobalCondition = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  [...slice(state).conditionsAndGroups, ...getConditionGroups(state).flatMap((group) => group.conditions)].includes(
    conditionId,
  );

export const getGlobalConditionIds = (state: RootStateOr<WorkbookState>) => {
  return Object.keys(getConditionsById(state))
    .map((key) => key as ConditionId)
    .filter((conditionId) => isGlobalCondition(state, conditionId));
};

export const getGlobalConditionsHavingValue = (state: RootStateOr<WorkbookState>) => {
  return Object.values(slice(state).conditionById).filter(
    (condition) => isGlobalCondition(state, condition.id) && conditionHasValues(condition),
  );
};

export const getGlobalSpatialConditionsHavingValueItems = (state: RootStateOr<WorkbookState>) => {
  return Object.values(slice(state).conditionById).filter(
    (condition) => isGlobalCondition(state, condition.id) && isSpatialConditionWithValueItems(condition),
  );
};

export const isFilteredByCondition = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  conditionId: ConditionId,
) => {
  return getDataViewIdsAffectedByCondition(state, conditionId).includes(dataViewId);
};

export const hasDataViewConditions = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).conditions.length > 0;
};

export const isDataViewAffectedByWorkbookConditions = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const globalAndLocalConditionsById = getConditionsById(state);
  const globalAndLocalConditionIds = Object.keys(globalAndLocalConditionsById) as ConditionId[];
  const globalConditionIds = globalAndLocalConditionIds.filter((conditionId) => isGlobalCondition(state, conditionId));

  // we check if there's at least one workbook condition that affects our dataView
  return globalConditionIds.some((conditionId) => isFilteredByCondition(state, dataViewId, conditionId));
};

export const getViews = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (items) => Object.values(items) as ViewDto[],
);
const getViewIds = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (viewById) => Object.keys(viewById) as ViewId[],
);
export const getActiveWorksheetViewIds = (state: RootStateOr<WorkbookState>) => getActiveWorksheet(state)?.views ?? [];
export const getActiveWorksheetViews = (state: RootStateOr<WorkbookState>) =>
  getActiveWorksheetViewIds(state).map((viewId) => getViewById(slice(state), viewId));
export const getActiveView = (state: RootStateOr<WorkbookState>) => {
  const activeViewId = getActiveViewId(state);
  if (activeViewId) {
    return getViewById(state, activeViewId);
  }
};

/**
 * Returns view id of active map view or id first NON-INTEGRATED map view (@see net/disy/cadenza/workbook/model/CommonViewSettingsKey#INTEGRATED)
 */
export const getActiveOrFirstMapViewId = createSelector([getActiveView, getViews], (activeView, views) => {
  if (activeView?.type === ViewType.MAP) {
    return activeView.id;
  }
  return views.find((view) => view.type === ViewType.MAP && !isIntegrated(view))?.id;
});

export const getViewById = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  addViewMetadata(state, slice(state).viewById[viewId])!;

/**
 * Returns the viewId of the given dataViewId.
 * Only views of the current worksheet are considered, if not in report mode.
 **/
export const getViewByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const views = isReportMode(state) ? getViews(state) : getActiveWorksheetViews(state);
  return findViewByDataViewId(state, views, dataViewId);
};

/**
 * Returns the viewId of the given dataViewId.
 * All views of any worksheet are considered, regardless of the report mode.
 **/
export const getViewByDataViewIdOfAllWorksheets = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return findViewByDataViewId(state, getViews(state), dataViewId);
};

const findViewByDataViewId = (state: RootStateOr<WorkbookState>, views: ViewDto[], dataViewId: DataViewId) => {
  return addViewMetadata(
    state,
    views.find((view) => view.dataViews.includes(dataViewId)),
  )!;
};

export const getViewByLayerId = (state: RootStateOr<WorkbookState>, layerId: LayerId) => {
  const views = isReportMode(state) ? getViews(state) : getActiveWorksheetViews(state);
  return addViewMetadata(
    state,
    views.find((view) => Object.keys(view.layerDefinitions ?? {}).includes(layerId)),
  )!;
};

export const getLayerGroupsFromView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return Object.values(view.layerDefinitions || []).filter((layer) => layer.structureType !== 'SINGLE');
};

export const getActiveLayerId = (state: RootStateOr<WorkbookState>) => {
  return slice(state).activeLayerId;
};

export const getActiveSubDataViewId = (state: RootStateOr<WorkbookState>) => {
  return slice(state).activeSubDataViewId;
};

export const getVisualisationKeyByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (!view) {
    return;
  }
  const dataViewId = view.dataViews[0];
  if (dataViewId) {
    return getDataViewById(state, dataViewId).visualizationKey;
  }
};

export const getVisualizationKeyByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).visualizationKey;
};

export const getViewTypeByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return requireNonNullable(getViewType(getVisualizationKeyByDataViewId(state, dataViewId)));
};

export const getViewNamesByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = slice(state).viewById[viewId];
  return {
    name: view?.name,
    defaultName: view?.defaultName,
  };
};

function addViewMetadata(state: RootStateOr<WorkbookState>, view?: ViewDto): ViewWithMetadata | undefined {
  if (view) {
    return { ...view, metadata: slice(state).viewMetadataById[view.id] ?? {} };
  }
}

export const getViewMetadata = <T extends ViewMetadata = ViewMetadata>(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
) => slice(state).viewMetadataById[viewId] as T | undefined;

export const getDataViewMetadata = <T extends DataViewMetadata = DataViewMetadata>(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
) => slice(state).dataViewMetadataById[dataViewId] as T | undefined;

export const isViewLoading = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const metadata = getViewMetadata(state, viewId);
  if (metadata && 'loadingState' in metadata) {
    return ['LOADING', 'LOADING_LONG'].includes(metadata.loadingState as LoadingState);
  }
  return false;
};

const isViewRendering = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const metadata = getViewMetadata(state, viewId);
  if (metadata && 'isRendering' in metadata) {
    return !!metadata.isRendering;
  }
  return false;
};

export const isAnyRelevantViewLoading = (state: RootStateOr<WorkbookState>, viewIds: ViewId[]) =>
  viewIds.some((viewId) => isViewLoading(state, viewId));

export const isAnyViewLoadingOrRendering = (state: RootStateOr<WorkbookState>) =>
  getViewIds(state).some((viewId) => isViewLoading(state, viewId) || isViewRendering(state, viewId));

export const getDataViewById = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  slice(state).dataViewById[dataViewId];

const getDataViewRequired = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const dataView = slice(state).dataViewById[dataViewId] as DataViewDto | undefined;
  assertNonNullable(dataView, `Data view with id ${dataViewId} does not exist`);
  return dataView;
};

export const getOnlyDataViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const dataViewIds = getDataViewIdsByViewId(state, viewId);
  assert(dataViewIds.length === 1, 'expected exactly one DataView');
  return dataViewIds[0];
};

export const getOnlyNoneAnalyticLineDataViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const dataViewIds = getDataViewIdsByViewId(state, viewId).filter(
    (dataViewId) => !isAnalyticLine(getVisualizationKeyByDataViewId(state, dataViewId)),
  );
  assert(dataViewIds.length === 1, 'expected exactly one none analytic line DataView');
  return dataViewIds[0];
};

export const getNoneAnalyticLineDataViewIds = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getDataViewIdsByViewId(state, viewId).filter(
    (dataViewId) => !isAnalyticLine(getVisualizationKeyByDataViewId(state, dataViewId)),
  );

export function getOnlyDataView(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  return getDataViewById(state, getOnlyDataViewId(state, viewId));
}

export const getDataViewStatus = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  slice(state).dataViewById[dataViewId].status;

export function hasWorkbookInvalidStatus() {
  const hasInvalidDataViews = Object.values(slice(getStore().getState()).dataViewById).some(
    (dataView) => dataView.status !== 'VALID',
  );
  const hasInvalidLayers = getViews(getStore().getState())
    .map((viewDto) => (viewDto.layerDefinitions ? Object.values(viewDto.layerDefinitions) : []))
    .flatMap((layerDtos) => layerDtos.flatMap((layerDto) => layerDto.status))
    .some((status) => status !== 'OK');
  const unavailableObjecttypeIds = getUnavailableObjecttypeIds(getStore().getState());

  // Warning when saving the workbook should not be shown if the attribute is of type unknown and not visible in the data manger.
  // (Attributes with data type unknown are always hidden in the data manager.
  // See cadenza.repository.web.service.BasicObjecttypeService.hideAttributeWithAttributeTypeUnknown)
  // We could use !(attribute.type === 'UNKNOWN' && !attribute.visibleInDataBrowser) if this changes.
  const hasInvalidAttributes = getAttributes(getStore().getState()).some(
    (attribute) => attribute.status !== 'OK' && attribute.type !== 'UNKNOWN',
  );

  const hasInvalidConditions = getOrderedConditions(getStore().getState())
    .map((condition) => condition.status)
    .some((status) => status !== 'VALID');
  return (
    hasInvalidDataViews ||
    hasInvalidLayers ||
    unavailableObjecttypeIds.length > 0 ||
    hasInvalidAttributes ||
    hasInvalidConditions
  );
}

export const getDataViewIdsUsingObjecttypes = (state: RootStateOr<WorkbookState>, objecttypeIds: ObjecttypeId[]) => {
  const dataViewIds = [];

  const objecttypeIdSet = new Set(objecttypeIds.map(objecttypeIdToString));

  for (const [dataViewId, dataView] of entries(slice(state).dataViewById)) {
    if (
      Object.values(dataView.attributes) // Only bundle attributes
        .flat() // Unwrap bundles attributes
        .some((attributeId) => objecttypeIdSet.has(getAttributeById(state, attributeId).objecttypeId))
    ) {
      dataViewIds.push(dataViewId);
    }
  }

  return dataViewIds;
};

export const getDataViewIdsByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getViewById(state, viewId)?.dataViews ?? [];

export const getDataViewsByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getDataViewIdsByViewId(state, viewId).map((dataViewId) => getDataViewById(state, dataViewId));

export function getViewIdsByWorksheetId(state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) {
  return getWorksheetById(state, worksheetId).views;
}

function getDataViewsByWorksheetId(state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) {
  return getViewIdsByWorksheetId(state, worksheetId).flatMap((viewId) => getDataViewIdsByViewId(state, viewId));
}

export function getMainObjecttypeIdsForActiveWorksheet(state: RootStateOr<WorkbookState>) {
  const worksheetId = getActiveWorksheetId(state);
  const dataViewIds = getDataViewsByWorksheetId(state, worksheetId);
  const objecttypeIds = dataViewIds
    .map((dataViewId) => getMainObjecttypeIdForDataView(state, dataViewId))
    .filter((objecttypeId) => isValidMainObjecttype(objecttypeId));
  return [...new Set(objecttypeIds)];
}

export function getMainObjecttypeIdForDataView(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  return getDataViewById(state, dataViewId).mainObjecttype.objecttypeId;
}

// returns undefined when the objecttypeId is VOID (the ot is not set for the dataView)
export const getValidMainObjecttypeIdForDataView = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const objecttypeId = getDataViewById(state, dataViewId).mainObjecttype.objecttypeId;
  return isValidMainObjecttype(objecttypeId) ? getObjecttypeById(state, objecttypeId) : undefined;
};

export const getNotificationsForViewByNotificationId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return slice(state).notificationsByView[viewId] ?? {};
};

export const getNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return Object.values(getNotificationsForViewByNotificationId(state, viewId));
};

export const layerHasErrorNotifications = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return getNotificationsForLayer(state, viewId, layerId).some((notification) => notification.level === 'ERROR');
};

export const getNotificationsForViewByTypes = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  types: NotificationType[],
) => {
  return getNotificationsForView(state, viewId).filter((notification) => types.includes(notification.type));
};

export const getDataViewNotificationsForViewByTypes = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  types: NotificationType[],
) => {
  return getNotificationsForViewByTypes(state, viewId, types).filter(isDataViewNotification);
};

export const getDataViewNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isDataViewNotification);
};

export const getDataViewNotificationForActiveView = (state: RootStateOr<WorkbookState>) => {
  const activeViewId = getActiveViewId(state);
  return activeViewId ? getDataViewNotificationsForView(state, activeViewId) : [];
};

const getLayerNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isLayerNotification);
};

export const getNotificationsForDataView = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  dataViewId: DataViewId,
) => {
  return getDataViewNotificationsForView(state, viewId).filter(
    (notification) => notification.dataViewId === dataViewId,
  );
};

export const getNotificationsForLayer = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return getLayerNotificationsForView(state, viewId).filter((notification) => notification.layerId === layerId);
};

export const getDataViewNamesByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const dataView = slice(state).dataViewById[dataViewId];
  return {
    name: dataView.name,
    defaultName: dataView.defaultName,
  };
};

export const getViewsAffectedByCondition = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) => {
  return getViewIdsAffectedByCondition(state, conditionId).map((viewId) => getViewById(state, viewId));
};

export const getObjecttypesByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const uniqueObjecttypes = new Set<Objecttype>();
  const attributeIds = Object.values(getDataViewById(state, dataViewId).attributes).flat();
  for (const attributeId of attributeIds) {
    const objecttype = getObjecttypeById(state, getAttributeById(state, attributeId).objecttypeId);
    if (objecttype) {
      uniqueObjecttypes.add(objecttype);
    }
  }
  return Array.from(uniqueObjecttypes);
};

export const getObjecttypesByViewId = createSelector(
  (state: RootStateOr<WorkbookState>) => state,
  getDataViewIdsByViewId,
  (state, dataViewIds): Objecttype[] => {
    return Array.from(
      dataViewIds.reduce((uniqueObjecttypes, dataViewId) => {
        getObjecttypesByDataViewId(state, dataViewId).forEach((ot) => uniqueObjecttypes.add(ot));
        return uniqueObjecttypes;
      }, new Set<Objecttype>()),
    );
  },
);

export const getConditionsByViewId = createSelector(
  getActiveWorksheetId,
  getConditionsById,
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (state: RootStateOr<WorkbookState>) => slice(state).affectedViewsByConditionId,
  (activeWorksheetId, conditionsById, viewById, affectedViewsByConditionId) => {
    const conditionsByViewId: Record<ViewId, ConditionDto[]> = {};

    Object.values(conditionsById).forEach((condition) => {
      const viewsAffectedByCondition =
        affectedViewsByConditionId[condition.id]?.affectedViews?.[activeWorksheetId]?.map(
          (viewId) => viewById[viewId],
        ) || [];

      viewsAffectedByCondition.forEach((view) => {
        if (!conditionsByViewId[view.id]) {
          conditionsByViewId[view.id] = [];
        }
        if (!conditionsByViewId[view.id].includes(condition)) {
          conditionsByViewId[view.id].push(condition);
        }
      });
    });

    return conditionsByViewId;
  },
);

export const hasConditionsWithMissingMandatoryValue = createSelector(getConditionsById, (conditionsById) => {
  for (const condition of Object.values(conditionsById)) {
    if (condition.settings.mandatory && !conditionHasValues(condition)) {
      return true;
    }
  }
  return false;
});

const getSpatialConditionsForAllViews = createSelector(getConditionsByViewId, (conditionsByViewId) => {
  const spatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
  entries(conditionsByViewId).forEach(([viewId, conditions]) => {
    const spatialConditions = conditions.filter((condition) => condition.type === 'PolygonCondition');
    if (spatialConditions.length) {
      spatialConditionsByViewId[viewId] = spatialConditions;
    }
  });
  return spatialConditionsByViewId;
});

const getEffectiveSpatialConditionsForAllViews = createSelector(
  getSpatialConditionsForAllViews,
  (spatialConditionsByViewId) => {
    const effectiveSpatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
    entries(spatialConditionsByViewId).forEach(([viewId, conditions]) => {
      const effectiveSpatialConditions = conditions.filter(isSpatialConditionEffective);
      if (effectiveSpatialConditions.length) {
        effectiveSpatialConditionsByViewId[viewId] = effectiveSpatialConditions;
      }
    });

    return effectiveSpatialConditionsByViewId;
  },
);

function isSpatialConditionEffective(spatialCondition: ConditionDto) {
  // it should have a value with at least one polygon
  return conditionHasValues(spatialCondition);
}

const getSpatialConditionsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getSpatialConditionsForAllViews(state)[viewId] ?? [];

export const getEffectiveSpatialConditionsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getEffectiveSpatialConditionsForAllViews(state)[viewId] ?? [];

const getOrderedConditions = (state: RootStateOr<WorkbookState>) =>
  getConditionsAndGroups(state)
    .flatMap((conditionOrGroupId) => {
      const conditionGroup = getConditionGroupById(state, conditionOrGroupId as ConditionGroupId);
      return conditionGroup ? conditionGroup.conditions : (conditionOrGroupId as ConditionId);
    })
    .map((conditionId) => getConditionById(state, conditionId));

export const viewHasEffectiveSpatialFilter = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getSpatialConditionsForView(state, viewId).length > 0;

export const getActiveViewId = (state: RootStateOr<WorkbookState>) => slice(state).activeViewId;
export const getActiveViewType = (state: RootStateOr<WorkbookState>) => {
  const activeViewId = slice(state).activeViewId;
  if (activeViewId) {
    return slice(state).viewById[activeViewId].type;
  }
};
export const isActiveView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => getActiveViewId(state) === viewId;

export const isViewInvalid = (state: RootStateOr<WorkbookState>, viewId: ViewId, ignoreSelectionChange = false) => {
  // Don't update views that aren't visible.
  if (isInvalidationSuppressedByMaximizedView(state, viewId)) {
    return false;
  }
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  const dataViewsAffectedBySelectionChange =
    ignoreSelectionChange ? [] : slice(state).dataViewsAffectedBySelectionChange;
  return (
    invalidViewsOrDataViews.includes(viewId) ||
    getViewById(state, viewId).dataViews.some(
      (dataViewId) =>
        invalidViewsOrDataViews.includes(dataViewId) || dataViewsAffectedBySelectionChange.includes(dataViewId),
    ) ||
    (slice(state).invalidLayerIdsByViewId[viewId] ?? []).length > 0
  );
};

export const isViewAffectedBySelection = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);
  return getDataViewIdsByViewId(state, viewId).some((dataViewId) =>
    dataViewsAffectedBySelectionChange.includes(dataViewId),
  );
};

export const getInvalidViewsOrDataViews = (state: RootStateOr<WorkbookState>) => slice(state).invalidViewsOrDataViews;
export const getInvalidViewsOrDataViewsOfActiveWorksheet = (state: RootStateOr<WorkbookState>) => {
  const viewIds = getActiveWorksheetViewIds(state);
  const dataViewIds = viewIds.flatMap((viewId) => getDataViewIdsByViewId(state, viewId));
  return slice(state).invalidViewsOrDataViews.filter(
    (viewIdOrDataViewId) =>
      viewIds.includes(viewIdOrDataViewId as ViewId) || dataViewIds.includes(viewIdOrDataViewId as DataViewId),
  );
};

export const isActiveWorksheetDirty = (state: RootStateOr<WorkbookState>) =>
  getInvalidViewsOrDataViewsOfActiveWorksheet(state).length > 0;
export const getDataViewsAffectedBySelectionChange = (state: RootStateOr<WorkbookState>) =>
  slice(state).dataViewsAffectedBySelectionChange;

export const getMaximizedViewId = (state: RootStateOr<WorkbookState>) => slice(state).maximizedViewId;
export const isMaximizedView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMaximizedViewId(state) === viewId;
export const isInvalidationSuppressedByMaximizedView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const maximizedViewId = getMaximizedViewId(state);
  return (
    maximizedViewId &&
    maximizedViewId !== viewId &&
    // controller view needs to be invalidated even if it is not maximized view because
    // it's invalidation triggers invalidation of connected views (e.g.: changing analysis context filter)
    getViewById(state, viewId).type !== 'CONTROLLER'
  );
};
export const hasForceMaximizedView = (state: RootStateOr<WorkbookState>) =>
  slice(state).forceMaximizePreviousState != null;

/* Whether the view is enlarged in linear layout (e.g. detail view). */
export const isDetailView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => getDetailViewId(state) === viewId;
export const getDetailViewId = (state: RootStateOr<WorkbookState>) => slice(state).detailViewId;
export const hasDetailView = (state: RootStateOr<WorkbookState>) => Boolean(getDetailViewId(state));

export const hasSelectionOrExternalLayerSelection = (state: RootStateOr<WorkbookState>) =>
  Boolean(slice(state).selection?.records || slice(state).selectionExternalLayer?.layerId);
export const getSelection = (state: RootStateOr<WorkbookState>) => slice(state).selection;
export const getSelectionExternalLayer = (state: RootStateOr<WorkbookState>) => slice(state).selectionExternalLayer;
export const getSelectionInOtherLayers = (state: RootStateOr<WorkbookState>) => slice(state).selectionInOtherLayers;

export const getDataViewIdsWithAllowedSelection = (state: RootStateOr<WorkbookState>) =>
  (Object.keys(slice(state).dataViewById) as DataViewId[]).filter((dataViewId) =>
    isDataSelectionEnabled(state, dataViewId),
  );

export function getViewSettingOrSettingDefault<T>(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  persistenceKey: string,
) {
  const viewDto = getViewById(state, viewId);
  return getSettingOrSettingDefault<T>(viewDto, persistenceKey);
}

export const isFeatureInfoEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName)!,
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_ENABLED'),
      'true',
    ) === 'true'
  );
};

/**
 * Returns true if any relevant attribute (in geometry bundle) of the dataView has the feature info enabled.
 * Note: For hierarchy, only the selected level attribute is tested.
 */
export const isFeatureInfoEnabledForDataView = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getAttributesFromBundles(state, dataViewId, 'GEOMETRY')
    .filter((attribute) => attribute.status === 'OK')
    .some((attribute) => {
      // for hierarchy, the selected level is checked
      return attribute.levels ?
          isFeatureInfoEnabledForAttribute(
            state,
            dataViewId,
            getAttributeById(
              getStore().getState(),
              createAttributeId(
                attribute.objecttypeId,
                requireNonNullable(attribute.levels.find((level) => level.name === attribute.selectedLevelName)).name,
              ),
            ),
          )
        : isFeatureInfoEnabledForAttribute(state, dataViewId, attribute);
    });
};

function isFeatureInfoEnabledForAttribute(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  { id, baseId }: WorkbookAttributeDto,
) {
  const { underlyingObjectTypeId, objecttypeId } = getAttributeById(state, baseId ?? id);
  const bundleName = getFeatureInfoAttributeBundleName(state, dataViewId, underlyingObjectTypeId ?? objecttypeId);
  return isFeatureInfoEnabled(state, dataViewId, bundleName);
}

// returns setting(s) (without default value) for a given key(s)
export const getDataViewSettings = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  keys: string | string[],
): Record<string, unknown> => {
  const settings = getDataViewById(state, dataViewId).settings;
  const result: Record<string, unknown> = {};
  (Array.isArray(keys) ? keys : [keys]).forEach((key) => {
    result[key] = getSetting(settings, key);
  });
  return result;
};

// returns setting value (without default value) for a given key
export function getDataViewSetting<T>(state: RootStateOr<WorkbookState>, dataViewId: DataViewId, key: string): T {
  return getDataViewSettings(state, dataViewId, key)[key] as T;
}

// returns setting value or default setting value for a given key
export function getDataViewSettingOrSettingDefault<T>(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  persistenceKey: string,
): T {
  return getSettingOrSettingDefault<T>(getDataViewById(state, dataViewId), persistenceKey);
}

// returns default setting value for a given key
export function getDataViewDefaultSetting<T>(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  key: string,
): T {
  return getDefaultSetting<T>(getDataViewById(state, dataViewId), key);
}

export const isFeatureInfoAutoModeEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName)!,
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_AUTO_MODE_ENABLED'),
      'true',
    ) === 'true'
  );
};

export const getFeatureInfoHeadlineType = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
): FeatureInfoHeadlineType => {
  if (isFeatureInfoAutoModeEnabled(state, dataViewId, bundleName)) {
    return 'auto';
  }
  return getSetting(
    getAttributeBundleSettings(state, dataViewId, bundleName)!,
    AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_TYPE'),
    'auto',
  );
};

export const getFeatureInfoCustomHeadline = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return getSetting<string>(
    getAttributeBundleSettings(state, dataViewId, bundleName)!,
    AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_CUSTOM'),
  );
};

export const getIsPieChartSliceValueResolvingEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName)!,
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_PIE_CHART_SLICE_RESOLVING_ENABLED'),
      'true',
    ) === 'true'
  );
};

export const isCombinationChart = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return (
    view &&
    view.type === 'CHART' &&
    view.dataViews.filter((dataViewId) =>
      CombinationChartDataViewVisualizationKeys.values.includes(getVisualizationKeyByDataViewId(state, dataViewId)),
    ).length > 1
  );
};

export const isDataViewInCurrentWorksheet = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const views = getActiveWorksheet(state).views.map((viewId) => getViewById(state, viewId));
  const dataViewIds = views.flatMap((view) => view.dataViews);
  return dataViewIds.includes(dataViewId);
};

export const isInCombinationChart = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return isCombinationChart(state, getViewByDataViewId(state, dataViewId).id);
};

export const getCombinationChartDataViews = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getDataViewsByViewId(state, viewId).filter((dataView) =>
    CombinationChartDataViewVisualizationKeys.values.includes(dataView.visualizationKey),
  );
};

export const getCombinationChartDataViewsInRenderOrder = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getCombinationChartDataViews(state, viewId).toReversed();
};

export const supportsMultipleDataView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return view.type === 'MAP' || isCombinationChart(state, viewId);
};

export const getFeatureInfoAttributeBundleName = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  objecttypeId: ObjecttypeIdString,
) => {
  return (getDataViewById(state, dataViewId).objecttypeIdToAttributeBundleName ?? {})[objecttypeId];
};

export const selectAttributeBundleSettings = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId)?.attributeBundleSettings;
};

export const dataViewPointSymbolization = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getSetting(getDataViewById(state, dataViewId), MapDataViewSettingsKey.toPersistenceKey('POINT_SYMBOLIZATION'));
};

export const getAttributeBundleSettings = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return getDataViewById(state, dataViewId)?.attributeBundleSettings[bundleName];
};

const getAttributeBundleDefinition = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return getDataViewById(state, dataViewId)?.attributeBundleDefinitions[bundleName];
};

export const getCommandErrorKey = (state: RootStateOr<WorkbookState>) => slice(state).commandErrorKey;
export const getCommandErrorDetails = (state: RootStateOr<WorkbookState>) => slice(state).commandErrorDetail;

export function isReportMode(state: RootStateOr<WorkbookState>) {
  return slice(state).reportMode;
}

export const layerById = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  const view = getViewById(state, viewId);
  return view.layerDefinitions[layerId];
};

export function getLayerName(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  return (
    getSetting<string>(layerById(state, viewId, layerId), LayerSettingsKey.toPersistenceKey('LAYER_NAME')) ??
    // Both layerName and dataView are not present for deleted referenced layers
    getDataViewById(state, layerId as DataViewId)?.name ??
    i18n('layerMissing')
  );
}

export const getLayerDataByLayerId = (state: RootStateOr<WorkbookState>, layerId: LayerId) => {
  const viewDto = getViewByLayerId(state, layerId);
  if (viewDto) {
    return getLayerData(state, viewDto.id, layerId);
  }
};

export interface LayerData {
  id: LayerId;
  definition: WorkbookLayerDto;
  dataView?: DataViewDto;
}

export const getLayerData = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId): LayerData => {
  return {
    id: layerId,
    definition: layerById(state, viewId, layerId),
    dataView: getDataViewById(state, toDataViewId(layerId)),
  };
};

export const getLastCommand = (state: RootStateOr<WorkbookState>) => {
  return slice(state).lastCommand;
};

export function canManageObjecttype(objecttypeId: ObjecttypeIdString) {
  const objecttype = getObjecttypeById(getStore().getState(), objecttypeId);
  return (
    objecttype != null &&
    isFeatureAvailable('ADMINISTRATION') &&
    isWorkbookVisible(getStore().getState()) &&
    hasPrivilege(objecttype.currentUserPrivileges, 'visible') &&
    !isObjecttypeBound(objecttype)
  );
}

export const isIntegratedView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return isIntegrated(getViewById(state, viewId));
};

export function isIntegrated(view: ViewDto) {
  return getSettingOrSettingDefault<boolean>(view, CommonViewSettingsKey.toPersistenceKey('INTEGRATED'));
}

export const getMapGeometryAttributeByDataViewById = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
): WorkbookAttributeDto | undefined => {
  const dataViewDto = getDataViewById(state, dataViewId);
  const geometryActiveAttributeId = dataViewDto?.attributes['GEOMETRY']?.[0];
  return geometryActiveAttributeId && getAttributeById(getStore().getState(), geometryActiveAttributeId);
};

export const hasMapGeometryAttribute = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return !!getMapGeometryAttributeByDataViewById(state, dataViewId);
};

export function conditionsAndGroupsModel(state: RootStateOr<WorkbookState>): (ConditionDto | ConditionGroupDto)[] {
  return getConditionsAndGroups(state).map(
    (id) => getConditionGroupById(state, id as ConditionGroupId) ?? getConditionById(state, id as ConditionId),
  );
}

export function isDataSelectionEnabled (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  return getDataViewSettingOrSettingDefault<boolean>(state, dataViewId,
    CommonDataViewSettingsKey.toPersistenceKey('SELECTION_ENABLED'),
  );
}
